import {gql} from '@apollo/client';
import {objectFromEntries} from '@benchling/js-utils';
import React, {useEffect, useMemo, useRef} from 'react';
import isEqual from 'react-fast-compare';

import DataConnector from 'connect/DataConnector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {FromServerRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import usePrevious from 'ui-library/hooks/usePrevious';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {
  ListAutomationOutputFiles,
  ListAutomationOutputFilesVariables,
} from './__generated/ListAutomationOutputFiles';
import {processAutomationOutputFile, setAutomationFileStatus} from './AutomationFileActions';
import getStatusForOutputFileButton from './getStatusForOutputFileButton';
import {
  isOutputFileMissingFolder,
  isOutputFileMissingFolderOrLocation,
  isOutputFileMissingLocation,
} from './isOutputFileMissingFolderOrLocation';

function isOutputFileUnprocessed(
  outputFile: AutomationOutputFileGraphQL,
  statusFromStore: AutomationFileStatusGraphQL | undefined
): boolean {
  return (
    getStatusForOutputFileButton(outputFile) === AutomationFileStatusGraphQL.pending &&
    statusFromStore !== AutomationFileStatusGraphQL.running &&
    outputFile.blob != null
  );
}

export const PROCESS_ALL_AUTOMATION_OUTPUT_FILES_QUERY = gql`
  query ListAutomationOutputFiles($automationOutputFileIds: [StrictString]!) @skipAudit {
    automationOutputFiles(ids: $automationOutputFileIds) {
      ... on AutomationOutputFileNode {
        id
        status
        transforms {
          id
          status
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProcessAllAutomationOutputFilesButtonProps {
  runId: string | null;
  locked: boolean;
  fileId?: string;
  automationOutputFiles: Array<AutomationOutputFileGraphQL>;
  handleRefreshVersionedData: () => Promise<void>;
}

interface QueryProps {
  isStale: boolean;
  onFileProcessed: (
    automationOutputFile: AutomationOutputFileGraphQL,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
}

/**
 * This component simply renders an upload button for uploading blobs to create output files
 */
const ProcessAllAutomationOutputFilesButton = (
  props: ProcessAllAutomationOutputFilesButtonProps & QueryProps & ConnectedProps
): JSX.Element | null => {
  const setStatusToRunning = (automationOutputFile: AutomationOutputFileGraphQL): void => {
    setAutomationFileStatus(automationOutputFile.id, AutomationFileStatusGraphQL.running);
  };
  const setStatusToPending = (automationOutputFile: AutomationOutputFileGraphQL): void => {
    setAutomationFileStatus(automationOutputFile.id, AutomationFileStatusGraphQL.pending);
  };
  const unprocessedFiles = props.automationOutputFiles.filter((aof) =>
    isOutputFileUnprocessed(aof, props.statusById[aof.id])
  );
  const processSingleFile = async (automationOutputFile: AutomationOutputFileGraphQL): Promise<void> => {
    try {
      const response = await processAutomationOutputFile(automationOutputFile.id, props.fileId);
      props.onFileProcessed(response!.automationOutputFile, response!.structuredTables);
      setAutomationFileStatus(response!.automationOutputFile.id, response!.automationOutputFile.status);
    } catch (err) {
      setStatusToPending(automationOutputFile);
      throw err;
    }
  };

  const processFiles = (): void => {
    for (const automationOutputFile of unprocessedFiles) {
      setStatusToRunning(automationOutputFile);
      processSingleFile(automationOutputFile);
    }
  };

  const _getTooltipText = (): string | null => {
    if (props.locked || props.isStale) {
      return null;
    }
    if (!unprocessedFiles.length) {
      return 'No files to be processed - upload files first';
    }

    const hasMissingFolders = unprocessedFiles.some((aof) => isOutputFileMissingFolder(aof));
    const hasMissingLocations = unprocessedFiles.some((aof) => isOutputFileMissingLocation(aof));
    let missingItem = '';
    if (hasMissingFolders && hasMissingLocations) {
      missingItem = 'Folder and location';
    } else if (hasMissingFolders) {
      missingItem = 'Folder';
    } else if (hasMissingLocations) {
      missingItem = 'Location';
    } else {
      return 'Process all pending output files';
    }
    return `${missingItem} must be set for all files to be processed`;
  };
  const shouldDisableButton = (): boolean => {
    if (props.locked) {
      return true;
    }
    if (props.isStale) {
      return false; // Button is never disabled if it's just a sync button (unless entry is locked)
    }
    if (!unprocessedFiles.length) {
      return true;
    }
    return unprocessedFiles.some((aof) => isOutputFileMissingFolderOrLocation(aof));
  };
  return (
    <Row
      className="u-mini-spacing-top"
      bufferRowReset={true}
      dataTestAttrs={{component: 'RunAutomationOutputFileUploader', element: 'root'}}
    >
      <Col md={3} />
      <Col md={9}>
        <TooltipWrapper tooltipTitle={_getTooltipText()}>
          <Button
            className="processAutomationFileButton"
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MINI}
            fullWidth={true}
            text={props.isStale ? 'Data is stale - click to sync run' : 'Process all'}
            icon="refresh"
            disabled={shouldDisableButton()}
            onClick={props.isStale ? props.handleRefreshVersionedData : processFiles}
          />
        </TooltipWrapper>
      </Col>
    </Row>
  );
};

interface ConnectedProps {
  statusById: Record<string, AutomationFileStatusGraphQL>;
}

class ProcessAllAutomationOutputFilesButtonConnector extends DataConnector<
  ProcessAllAutomationOutputFilesButtonProps & {
    updateAutomationOutputFiles: UpdateAutomationOutputFilesFunction;
  },
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    const statusById: Record<string, AutomationFileStatusGraphQL> = {};
    for (const automationOutputFile of this.props.automationOutputFiles) {
      statusById[automationOutputFile.id] =
        this.reduxState().ui.automationFileStatus[automationOutputFile.id];
    }
    return {
      statusById,
    };
  }
}

type UpdateAutomationOutputFilesFunction = (
  outputFiles: Array<AutomationOutputFileGraphQL>,
  structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
) => void;

const QueryingProcessAllAutomationOutputFilesButton = (
  props: ProcessAllAutomationOutputFilesButtonProps &
    ConnectedProps & {updateAutomationOutputFiles: UpdateAutomationOutputFilesFunction}
): JSX.Element => {
  const updateHandlerRef = useRef<UpdateAutomationOutputFilesFunction>(props.updateAutomationOutputFiles);
  const unprocessedFiles = props.automationOutputFiles.filter((aof) =>
    isOutputFileUnprocessed(aof, props.statusById[aof.id])
  );
  updateHandlerRef.current = props.updateAutomationOutputFiles;
  const response = useSkippableBenchlingQuery<ListAutomationOutputFiles, ListAutomationOutputFilesVariables>(
    PROCESS_ALL_AUTOMATION_OUTPUT_FILES_QUERY,
    {
      variables: {automationOutputFileIds: unprocessedFiles.map((aof) => aof.id)},
      skip: !unprocessedFiles.length,
    }
  );

  const liveAutomationOutputFilesById = useMemo(
    () =>
      response.type === QueryResultType.SUCCESS
        ? objectFromEntries(accessibleObjs(response.data.automationOutputFiles).map((aof) => [aof.id, aof]))
        : {},
    [response]
  );

  const prevLiveAutomationOutputFilesById = usePrevious(liveAutomationOutputFilesById);
  const isStale = unprocessedFiles.some(
    (aof) =>
      liveAutomationOutputFilesById[aof.id] &&
      getStatusForOutputFileButton(liveAutomationOutputFilesById[aof.id]) !==
        getStatusForOutputFileButton(aof)
  );
  useEffect(() => {
    if (isEqual(liveAutomationOutputFilesById, prevLiveAutomationOutputFilesById)) {
      return;
    }
    for (const aof of Object.values(liveAutomationOutputFilesById)) {
      if (
        !prevLiveAutomationOutputFilesById ||
        !isEqual(liveAutomationOutputFilesById[aof.id], prevLiveAutomationOutputFilesById[aof.id])
      ) {
        setAutomationFileStatus(aof.id, getStatusForOutputFileButton(liveAutomationOutputFilesById[aof.id]));
      }
    }
  }, [liveAutomationOutputFilesById, prevLiveAutomationOutputFilesById]);

  return (
    <ProcessAllAutomationOutputFilesButton
      {...props}
      onFileProcessed={(outputFile, structuredTables) => {
        updateHandlerRef.current([outputFile], structuredTables);
        response.refetch({automationOutputFileIds: unprocessedFiles.map((aof) => aof.id)});
      }}
      isStale={isStale}
    />
  );
};

export default ProcessAllAutomationOutputFilesButtonConnector.connect(
  QueryingProcessAllAutomationOutputFilesButton
);
